import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LeafIcon, RotateCcwIcon, TruckIcon } from 'lucide-react';
import { useCart } from '../../contexts/CartContext';
import { formatPrice } from '../../utils/currency';
import { getProduct } from '../../utils/products';
import type { Product } from '../../types/product';
import { Stars } from '../shop/Stars';
import { QuantityStepper } from '../shop/QuantityStepper';
const badgeLabel: Record<string, string> = {
  bestseller: 'Bestseller',
  nieuw: 'Nieuw',
  cadeau: 'Cadeau-tip'
};
export function BuyBox({
  product
}: {
  product: Product;
}) {
  const {
    add
  } = useCart();
  const navigate = useNavigate();
  const [variantId, setVariantId] = useState(product.variants[0].id);
  const [qty, setQty] = useState(1);
  const variant = product.variants.find(v => v.id === variantId) ?? product.variants[0];
  const included = (product.includes ?? []).map(s => getProduct(s)).filter((p): p is Product => Boolean(p));
  const isSizeVariant = /\d/.test(variant.label);
  const buyNow = () => {
    add(product.slug, variant.id, qty, false);
    navigate('/afrekenen');
  };
  return <div data-id="element-267" data-mp="1du5sorcmngee">
      {product.badges.length > 0 && <div className="flex gap-2" data-id="element-268" data-mp="18u9rt0z88532">
          {product.badges.map(b => <span key={b} className="rounded-full bg-surface px-2.5 py-1 text-xs font-medium" data-id="element-269" data-mp="18wdzlu08xo8m">{badgeLabel[b]}</span>)}
        </div>}
      <h1 className="mt-4 font-display text-4xl uppercase tracking-display text-ink sm:text-5xl" data-id="element-270" data-mp="zuwn0lm1s4oo">{product.name}</h1>
      <p className="mt-1.5 text-lg text-muted" data-id="element-271" data-mp="v1k3gpw86o2s">{product.subtitle}</p>
      <div className="mt-4" data-id="element-272" data-mp="q3a7id2ofjg4">
        <a href="#details" className="inline-block" data-id="element-273" data-mp="q4l7e1t0471y">
          <Stars rating={product.rating} reviews={product.reviews} size="md" data-id="element-274" data-mp="l5h1bk1dipfu" />
        </a>
      </div>

      <p className="mt-6 flex items-baseline gap-3" data-id="element-275" data-mp="h3vh1dmif8vm">
        <span className="text-3xl font-semibold tracking-tight tabular-nums" data-id="element-276" data-mp="h2kgdmod2h8q">{formatPrice(variant.price * qty)}</span>
        {variant.compareAt && <span className="text-lg text-muted line-through" data-id="element-277" data-mp="2gwu9vyll2n8">{formatPrice(variant.compareAt * qty)}</span>}
        {qty > 1 && <span className="text-sm text-muted" data-id="element-278" data-mp="6ii9vp2inv14">{qty} × {formatPrice(variant.price)}</span>}
      </p>
      <p className="mt-4 text-lg leading-relaxed text-ink" data-id="element-279" data-mp="3oooc3zgef5pw">{product.benefit}</p>

      {product.variants.length > 1 && <fieldset className="mt-8" data-id="element-280" data-mp="3tnr8kvibc3m9">
          <legend className="text-sm font-medium" data-id="element-281" data-mp="3tosrfm5kluej">
            {isSizeVariant ? 'Inhoud' : 'Variant'}: <span className="text-muted" data-id="element-282" data-mp="l9ij3wb37syb">{variant.label}</span>
          </legend>
          <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3" data-id="element-283" data-mp="q5fz21ifuxak">
            {product.variants.map(v => {
          const active = v.id === variant.id;
          return <button key={v.id} type="button" role="radio" aria-checked={active} onClick={() => setVariantId(v.id)} className={`flex flex-col items-start rounded-xl border px-4 py-3 text-left transition-colors duration-150 ${active ? 'border-ink bg-white ring-1 ring-ink' : 'border-line bg-white hover:border-muted'}`} data-id="element-284" data-mp="gev5wtljakz1">
                  <span className="text-sm font-semibold" data-id="element-285" data-mp="32e65pc1nujdo">{v.label}</span>
                  <span className="text-sm text-muted" data-id="element-286" data-mp="2ydcjqo7s3enf">{formatPrice(v.price)}</span>
                </button>;
        })}
          </div>
        </fieldset>}

      {included.length > 0 && <div className="mt-8" data-id="element-287" data-mp="25q8jewjmrohz">
          <p className="text-sm font-medium" data-id="element-288" data-mp="25r1fn3kyqvlv">In deze set</p>
          <ul className="mt-3 flex flex-wrap gap-2" data-id="element-289" data-mp="2jp86hzrhvvc1">
            {included.map(p => <li key={p.slug} data-id="element-290" data-mp="2epdgdyfjn9s1">
                <Link to={`/product/${p.slug}`} className="flex items-center gap-2 rounded-full border border-line bg-white py-1 pl-1 pr-3 text-sm hover:border-ink" data-id="element-291" data-mp="2eobsuuhccutf">
                  <img src={p.image} alt="" className="h-7 w-7 rounded-full object-cover" data-id="element-292" data-mp="3q5k0donaar1t" />
                  {p.name}
                </Link>
              </li>)}
          </ul>
        </div>}

      <div className="mt-8 flex gap-3" data-id="element-293" data-mp="u84ajov93yhk">
        <QuantityStepper value={qty} onChange={setQty} size="lg" data-id="element-294" data-mp="ub05r5fps92b" />
        <button type="button" onClick={() => add(product.slug, variant.id, qty)} className="h-14 flex-1 whitespace-nowrap rounded-full bg-ink px-6 text-base font-medium text-canvas transition-colors duration-150 hover:bg-berry" data-id="element-295" data-mp="z6z3hjmxn3fe">
          In winkelwagen
        </button>
      </div>
      <button type="button" onClick={buyNow} className="mt-3 h-14 w-full whitespace-nowrap rounded-full border border-ink/15 bg-white text-base font-medium text-ink transition-colors duration-150 hover:border-ink" data-id="element-296" data-mp="1d5yp0if5f27c">
        Direct afrekenen
      </button>

      <p className="mt-5 flex items-center gap-2 text-sm" data-id="element-297" data-mp="1rxz9g04tqh9p">
        <span className="h-2 w-2 rounded-full bg-olive" aria-hidden="true" data-id="element-298" data-mp="1vxztc0aozko5" />
        <span data-id="element-299" data-mp="1vvwtypnngf6r">
          <strong className="font-medium text-olive" data-id="element-300" data-mp="3gg22c8rg8jic">Op voorraad</strong> — vóór 22:00 besteld, morgen in huis
        </span>
      </p>

      <ul className="mt-8 grid gap-3 border-t border-line pt-6 text-sm text-muted" data-id="element-301" data-mp="3cdnlfu7cbttp">
        <li className="flex items-center gap-3" data-id="element-302" data-mp="37eu4tswrbmpb"><TruckIcon className="h-4 w-4 text-ink" data-id="element-303" data-mp="37fvq0q7hlpkr" /> Gratis verzending vanaf € 50</li>
        <li className="flex items-center gap-3" data-id="element-304" data-mp="32i3nz3d4on2u"><RotateCcwIcon className="h-4 w-4 text-ink" data-id="element-305" data-mp="3yx388cnvn2l" /> 30 dagen bedenktijd</li>
        <li className="flex items-center gap-3" data-id="element-306" data-mp="40zqwlqcd445"><LeafIcon className="h-4 w-4 text-ink" data-id="element-307" data-mp="3v4oek1ls4v5c" /> 100% puur, zonder toevoegingen</li>
      </ul>

      <div className="fixed inset-x-0 bottom-0 z-30 flex items-center gap-3 border-t border-line bg-canvas/95 px-5 py-3 backdrop-blur lg:hidden" data-id="element-308" data-mp="3qe82olxlxhl3">
        <div className="min-w-0 flex-1" data-id="element-309" data-mp="3levenxv024gx">
          <p className="truncate text-sm font-medium" data-id="element-310" data-mp="mpejvek7ys7l">{product.name} · {variant.label}</p>
          <p className="text-sm font-semibold tabular-nums" data-id="element-311" data-mp="mr7mmirposab">{formatPrice(variant.price * qty)}</p>
        </div>
        <button type="button" onClick={() => add(product.slug, variant.id, qty)} className="h-12 whitespace-nowrap rounded-full bg-ink px-6 text-sm font-medium text-canvas" data-id="element-312" data-mp="2c6s5cjub8adk">
          In winkelwagen
        </button>
      </div>
    </div>;
}